import type { ReactNode } from 'react';
import type { TenantConfig } from '@yourstore/domain';
import { Features } from './setup/Features';
import { TenantSettings } from './setup/Settings';
import { BookingRule } from './setup/BookingRule';
import { BusinessDetails } from './setup/BusinessDetails';
import { CloseBusiness } from './setup/CloseBusiness';
import { AppLook } from './setup/look/AppLook';
import { Logins } from './setup/Logins';
import type { TenantDetail as TenantDetailWire } from '../../wire';
import './setup/setup.css';

/**
 * **Setup** — how this business works (console redesign B2): what it has
 * switched on, what its counter, shelf, kitchen and website do, how its day is
 * offered, how its staff app looks (B8), who can sign in to it (the staff
 * logins, next to the app's look because both are about the app the owner's
 * people hold), the facts it was created with, and the way out.
 *
 * Two columns, as the mockup: Features on the left, because it decides what
 * the right column shows — the settings appear by the feature they belong to.
 *
 * Every card reads the tenant payload already on screen and reloads the page
 * after a write, because what they re-render from is on it. The settings and
 * the booking rule both write `tenant_config.config`; the rule's simulator
 * reads nothing else — the day it runs on is typed in, because `admin_api`
 * has no privilege on the book and should not (`OfferSimulator.tsx`).
 */
export function SetupTab({
  tenantId,
  detail,
  onChanged,
}: {
  tenantId: string;
  detail: TenantDetailWire;
  onChanged: () => void;
}): ReactNode {
  const config = (detail.config?.config ?? null) as Partial<TenantConfig> | null;
  const { tenant } = detail;
  const enabled = new Set(detail.modules.filter((m) => m.status !== 'off').map((m) => m.module));
  return (
    <div className="setup">
      <Features
        tenantId={tenantId}
        name={tenant.name}
        modules={detail.modules}
        onChanged={onChanged}
      />
      <div className="setup__side">
        <TenantSettings
          tenantId={tenantId}
          name={tenant.name}
          config={config}
          enabled={enabled}
          onChanged={onChanged}
        />
        <BookingRule
          tenantId={tenantId}
          name={tenant.name}
          timezone={tenant.timezone}
          config={config}
          onChanged={onChanged}
        />
        <AppLook tenantId={tenantId} name={tenant.name} onChanged={onChanged} />
        <Logins tenantId={tenantId} name={tenant.name} />
        <BusinessDetails
          name={tenant.name}
          timezone={tenant.timezone}
          currency={tenant.currency}
          plan={tenant.plan}
          status={tenant.status}
          authority={tenant.authority}
          catalogVersion={detail.config?.catalog_version ?? null}
        />
        <CloseBusiness
          tenantId={tenantId}
          name={tenant.name}
          status={tenant.status}
          isProtected={tenant.protected}
          onChanged={onChanged}
        />
      </div>
    </div>
  );
}
